// =====================================================================
// My Agency V2 — card family (ARTBOARD-SPEC §4 CMP-CARD, CMP-STAT,
// CMP-CONTACT + copy toast). Purely presentational.
// =====================================================================
import React from "react";
import { ChevronRightIcon, MailIcon, PhoneIcon, CopyIcon, CheckIcon } from "./icons";

// CMP-CARD grid: auto-fill minmax(330px,1fr) gap 16 (partner wall:
// minmax(215px,1fr), margin-top 14). "Composed at 2 cards, dense at 40."
export const CardGrid = ({ min = 330, gap = 16, marginTop = 18, style, children, ...rest }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fill,minmax(${min}px,1fr))`,
      gap,
      marginTop,
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

// CMP-CARD — white content card. `clickable` adds hover lift.
// gap: 13 default (player cards 11; partner wall uses its own layout).
export const Card = ({ clickable = false, gap = 13, className = "", style, children, ...rest }) => (
  <div
    className={[
      "bg-white border border-[#E3E9F1] rounded-xl shadow-[0_1px_2px_rgba(15,23,42,.05)] p-4 flex flex-col min-w-0",
      clickable
        ? "cursor-pointer hover:border-[#B9D8FF] hover:shadow-[0_4px_14px_rgba(15,23,42,.08)]"
        : "",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    style={{ gap, ...style }}
    {...rest}
  >
    {children}
  </div>
);

// Card header row: avatar → identity column → trailing action.
export const CardHeader = ({ gap = 12, identityGap = 5, avatar, action, style, children, ...rest }) => (
  <div style={{ display: "flex", gap, alignItems: "flex-start", ...style }} {...rest}>
    {avatar}
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: identityGap,
      }}
    >
      {children}
    </div>
    {action}
  </div>
);

// Card name line: 15px/600 ellipsis (mobile 15.5px).
export const CardName = ({ mobile = false, style, children, ...rest }) => (
  <span
    style={{
      fontSize: mobile ? 15.5 : 15,
      fontWeight: 600,
      color: "#0F172A",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      ...style,
    }}
    {...rest}
  >
    {children}
  </span>
);

// CMP-STAT — stat chip row ("Managing players" / "Managed by").
// value: pass a node (number, or avatar+name for "Managed by").
// `muted` renders the value in #94A3B8 (the artboard's "0" treatment —
// derived from REAL data at the call site, never a rule from the mock).
export const StatChip = ({
  label,
  value,
  muted = false,
  interactive = false,
  chevron = interactive,
  padding = "9px 12px",
  style,
  ...rest
}) => (
  <div
    className={
      interactive
        ? "cursor-pointer hover:border-[#B9D8FF] hover:bg-[#F0F7FF]"
        : undefined
    }
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "#F7FAFD",
      border: "1px solid #EBF0F6",
      borderRadius: 10,
      padding,
      ...style,
    }}
    {...rest}
  >
    <span style={{ fontSize: 12.5, fontWeight: 500, color: "#64748B" }}>{label}</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ fontSize: 17, fontWeight: 700, color: muted ? "#94A3B8" : "#0F172A" }}>
        {value}
      </span>
      {chevron && <ChevronRightIcon size={14} />}
    </span>
  </div>
);

// CMP-CONTACT — contact row with copy affordance.
// kind: "email" | "phone" (picks the verbatim icon). Copy wiring stays
// at the call site (existing navigator.clipboard logic) via `onCopy`.
// Renders nothing without a value (no N/A rows — FUNCTION-INVENTORY 2.4).
// `display` (optional) renders a decorated node (e.g. the board-1b
// search-match <mark> highlight) while copy still uses the raw value.
export const ContactRow = ({ kind = "email", value, display, copyTitle, onCopy, mobile = false, style, ...rest }) => {
  if (!value) return null;
  const Icon = kind === "phone" ? PhoneIcon : MailIcon;
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, ...style }}
      {...rest}
    >
      <Icon size={mobile ? 15 : 14} style={{ flex: "none" }} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: mobile ? 13.5 : 13,
          color: "#475569",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {display ?? value}
      </span>
      {onCopy && (
        <button
          type="button"
          title={copyTitle}
          onClick={(e) => {
            e.stopPropagation();
            onCopy(value);
          }}
          className={`inline-flex flex-none border-0 bg-transparent rounded-md cursor-pointer hover:opacity-100 hover:bg-[#F1F5F9] ${
            mobile ? "p-1.5 opacity-[.45]" : "p-1 opacity-40"
          }`}
        >
          <CopyIcon size={mobile ? 14 : 13} />
        </button>
      )}
    </div>
  );
};

// Copy feedback toast (board 1f): "Copied to clipboard".
export const CopyToast = ({ children, style, ...rest }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 7,
      fontSize: 11.5,
      color: "#15803D",
      background: "#ECFDF5",
      borderRadius: 8,
      padding: "6px 10px",
      width: "fit-content",
      ...style,
    }}
    {...rest}
  >
    <CheckIcon size={12} strokeWidth={2.4} />
    {children}
  </div>
);
